<template>
  <div v-if="comparingLabel" class="ComparisonCardsMobile-comparing">
    <ActionButton
      variant="tertiary"
      size="sm"
      leftIcon="chevron-left"
      @click="emit('back')"
    >
      {{ comparingLabel }}
    </ActionButton>
  </div>

  <div ref="scrollArea" class="ComparisonCardsMobile-cards">
    <div ref="track" class="ComparisonCardsMobile-cardsTrack">
      <article
        v-for="column of columns"
        :key="column.planId"
        class="ComparisonCardsMobile-card"
        :class="column.presentation.stateClass"
      >
        <header class="ComparisonCardsMobile-cardHeader">
          <ColumnHeader :column :tagProps="column.presentation.tag" />
        </header>

        <div
          v-for="(row, index) of rows"
          :key="index"
          class="ComparisonCardsMobile-stackRow"
        >
          <span class="ComparisonCardsMobile-stackLabel">{{ row.label }}</span>
          <span class="ComparisonCardsMobile-stackValue">
            <PriceCell
              v-if="row.isPrice"
              :price="column.price"
              :accent="column.state === 'promotion'"
            />
            <template v-else>{{ column.cells[index] ?? EMPTY_CELL }}</template>
          </span>
        </div>

        <div v-if="column.submitLabel" class="ComparisonCardsMobile-cardCta">
          <ActionButton
            :variant="column.presentation.ctaVariant"
            :disabled="column.submitDisabled"
            size="md"
            class="ComparisonCardsMobile-cta"
            @click="emit('submit', column.planId)"
          >
            {{ column.submitLabel }}
          </ActionButton>
        </div>
      </article>
    </div>
  </div>

  <PageIndicator
    v-if="pageCount > 1"
    :modelValue="activePage"
    :count="pageCount"
    :dotLabel="pageLabel"
    class="ComparisonCardsMobile-pages"
    @update:model-value="scrollToPage"
  />
</template>

<script setup lang="ts">
import { watch, nextTick, useTemplateRef } from 'vue';
import ActionButton from '@cvo/ui/components/atoms/ActionButton/ActionButton.vue';
import PageIndicator from '@cvo/ui/components/atoms/PageIndicator/PageIndicator.vue';
import { usePagedScroll } from '@cvo/ui/composables';
import ColumnHeader from './ColumnHeader.vue';
import PriceCell from './PriceCell.vue';
import { EMPTY_CELL } from './BasePlanComparisonTable.model';
import type { ComparisonLayoutProps } from './BasePlanComparisonTable.model';

export interface ComparisonCardsMobileProps extends ComparisonLayoutProps {
  /** Heading next to the back control. */
  comparingLabel?: string;
}

const props = defineProps<ComparisonCardsMobileProps>();

const emit = defineEmits<{
  (e: 'submit', planId: string): void;
  (e: 'back'): void;
}>();

const scrollArea = useTemplateRef<HTMLElement>('scrollArea');
const track = useTemplateRef<HTMLElement>('track');

// The track holds only cards, so every child is a page.
const { activePage, pageCount, measurePages, scrollToPage } = usePagedScroll({ scrollArea, track });

// New columns or rows repage the track without resizing it, so the resize observer never fires.
watch(() => [props.columns, props.rows], () => nextTick(measurePages));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .ComparisonCardsMobile {
    &-comparing {
      display: flex;
      justify-content: flex-start;
    }

    // One card per page: swiping snaps to the next plan, so the scrollbar is hidden.
    &-cards {
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &-cardsTrack {
      display: flex;
      gap: vars.$dimensions-d-4;
      align-items: stretch;
    }

    &-card {
      display: flex;
      flex-direction: column;
      flex: 0 0 100%;
      background-color: vars.$organisms-table-comparison-table-content-bg-color;
      border: 1px solid vars.$organisms-table-comparison-table-border-color;
      border-radius: vars.$dimensions-d-3;
      overflow: hidden;
      scroll-snap-align: start;
    }

    &-cardHeader {
      padding: vars.$dimensions-d-5;
      border-block-end: 1px solid vars.$organisms-table-comparison-table-content-border-color;
    }

    &-stackRow {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-1;
      padding: vars.$dimensions-d-3 vars.$dimensions-d-5;
      border-block-end: 1px solid vars.$organisms-table-comparison-table-content-border-color;
    }

    &-stackLabel {
      @include text.text('xs', vars.$typography-weight-strong);
      color: vars.$organisms-table-comparison-table-content-label-color;
    }

    &-stackValue {
      @include text.text('xs');
      color: vars.$organisms-table-comparison-table-content-text-color;
    }

    &-card.is-comingSoon &-stackValue {
      color: vars.$organisms-table-comparison-table-content-comingsoon-text-color;
    }

    &-cardCta {
      padding: vars.$dimensions-d-4 vars.$dimensions-d-5;
    }

    &-cta {
      width: 100%;
    }

    &-pages {
      flex-shrink: 0;
    }
  }
}
</style>
